import { ColumnDef } from "@tanstack/react-table";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Button } from "../../button";
import { ArrowUpDown, Trash } from "lucide-react";
import { t } from "i18next";
import TableWrapper from "../TableWrapper";
import { useTranslation } from "react-i18next";
import { MusterPoint, TABLE_IDS } from "../../../constants/common";
import DeleteMusterPointModal from "../../modal/DeleteMusterPointModal";

interface MusterPointsConfigurationTableProps {
  musterPoints?: MusterPoint[];
  setSelectedMusterPointId: Dispatch<SetStateAction<number>>;
  page: number;
  pages: number;
  setPage: Dispatch<SetStateAction<number>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  count: number;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
}

const MusterPointsConfigurationTable = ({
  musterPoints,
  setSelectedMusterPointId,
  pages,
  page,
  setPage,
  search,
  setSearch,
  count,
  pageSize,
  setPageSize,
}: MusterPointsConfigurationTableProps) => {
  const { i18n } = useTranslation();

  const [isDeleteMusterPointModalOpen, setIsDeleteMusterPointModalOpen] =
    useState(false);
  const [selectedDeleteMusterPointId, setSelectedDeleteMusterPointId] =
    useState(0);

  const handleDeleteMusterPoint = useCallback(async (musterPointId: number) => {
    setIsDeleteMusterPointModalOpen(true);
    setSelectedDeleteMusterPointId(musterPointId);
  }, []);

  const columns: ColumnDef<MusterPoint>[] = useMemo(
    () => [
      {
        accessorKey: "n_MusterPointID",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("musterPointId")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span
              onClick={() =>
                setSelectedMusterPointId(row.original.n_MusterPointID)
              }
              className="text-blue-800 underline cursor-pointer capitalize"
            >
              {row.original.n_MusterPointID}
            </span>
          );
        },
      },
      {
        accessorKey: "s_MusterPointName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("musterPointNameEn")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
      },
      {
        accessorKey: "s_MusterPointName_Ar",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("musterPointNameAr")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
      },
      {
        accessorKey: "s_MusterPointMapLink",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("musterPointLink")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
      },
      {
        accessorKey: "s_LocationName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("location")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_LocationName_En
                : row.original.s_LocationName_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "s_BuildingNames_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("buildings")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const buildings =
            i18n.language === "en"
              ? row.original.s_BuildingNames_En?.split(", ")
              : row.original.s_BuildingNames_Ar?.split(", ");

          return (
            <div className="flex flex-col space-y-1">
              {buildings?.map((buildingName, index) => (
                <div key={index} className="whitespace-nowrap">
                  {buildingName}
                </div>
              ))}
            </div>
          );
        },
      },
      {
        header: t("action"),
        cell: ({ row }) => {
          return (
            <Trash
              onClick={() =>
                handleDeleteMusterPoint(row.original.n_MusterPointID)
              }
              className="w-5 h-5 text-red-600 cursor-pointer"
            />
          );
        },
      },
    ],
    [handleDeleteMusterPoint, i18n.language, setSelectedMusterPointId],
  );

  return (
    <>
      <TableWrapper
        data={musterPoints}
        columns={columns}
        isColumnChooser={false}
        search={search}
        setSearch={setSearch}
        page={page}
        setPage={setPage}
        totalPages={pages}
        count={count}
        isSearch
        tableId={TABLE_IDS.MUSTER_POINTS_CONFIG}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />

      <DeleteMusterPointModal
        musterPointId={selectedDeleteMusterPointId}
        isOpen={isDeleteMusterPointModalOpen}
        onOpenChange={() => setIsDeleteMusterPointModalOpen(false)}
      />
    </>
  );
};

export default React.memo(MusterPointsConfigurationTable);
